import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// Extend dayjs with UTC and timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const formatTime = (value: Date | string | number, format: string = 'MM-DD-YYYY') => {
    if (!value) return '';
    return dayjs(value).format(format);
}

/**
 * Convert local datetime string to UTC format for API
 * @param localDateTimeString - Local datetime string in format "YYYY-MM-DD HH:mm:ss"
 * @returns UTC datetime string in format "YYYY-MM-DD HH:mm:ss"
 */
const convertLocalToUTC = (localDateTimeString: string): string => {
    if (!localDateTimeString) return '';
    
    // Parse the local datetime string and convert to UTC
    const utcDateTime = dayjs(localDateTimeString).utc().format('YYYY-MM-DD HH:mm:ss');
    return utcDateTime;
}

/**
 * Convert UTC datetime string to local format for display
 * @param utcDateTimeString - UTC datetime string in format "YYYY-MM-DD HH:mm:ss"
 * @returns Local datetime string in format "YYYY-MM-DD HH:mm:ss"
 */
const convertUTCToLocal = (utcDateTimeString: string): string => {
    if (!utcDateTimeString) return '';
    
    // Parse the UTC datetime string and convert to local
    const localDateTime = dayjs.utc(utcDateTimeString).local().format('YYYY-MM-DD HH:mm:ss');
    return localDateTime;
}

/**
 * Merge a local date and HH:mm:ss time into a UTC ISO string for API submission.
 * Returns an empty string when either input is missing or invalid.
 */
export const mergeDateAndTimeToISOString = (
    dateValue: Date | string | null | undefined,
    timeValue: string,
): string => {
    if (!dateValue || !timeValue) return '';

    const dateOnly = dayjs(dateValue).format('YYYY-MM-DD');
    const merged = dayjs(`${dateOnly}T${timeValue}`, 'YYYY-MM-DDTHH:mm:ss', true);

    if (!merged.isValid()) return '';

    return merged.toDate().toISOString();
}

/**
 * Merge a local date and HH:mm:ss time into a local DateTimeOffset string for C# APIs.
 * Example output: 2026-08-04T01:00:00+07:00
 */
export const mergeDateAndTimeToDateTimeOffsetString = (
    dateValue: Date | string | null | undefined,
    timeValue: string,
    timeZone?: string,
): string => {
    if (!dateValue || !timeValue) return "";

    const dateOnly = dayjs(dateValue).format("YYYY-MM-DD");
    const merged = timeZone
        ? dayjs.tz(`${dateOnly}T${timeValue}`, "YYYY-MM-DDTHH:mm:ss", timeZone)
        : dayjs(`${dateOnly}T${timeValue}`, "YYYY-MM-DDTHH:mm:ss", true);

    if (!merged.isValid()) return "";

    return merged.format("YYYY-MM-DDTHH:mm:ssZ");
};

export const buildEventDateTimeFields = (
    eventDate: Date | string | null | undefined,
    start: string,
    end: string,
    checkInTime?: string,
    timeZone?: string,
) => {
    const startTime = mergeDateAndTimeToDateTimeOffsetString(eventDate, start, timeZone);
    const endTime = mergeDateAndTimeToDateTimeOffsetString(eventDate, end, timeZone);
    const eventDateTimeOffset = mergeDateAndTimeToDateTimeOffsetString(eventDate, start, timeZone);
    const checkInTimeOffset = checkInTime ? mergeDateAndTimeToDateTimeOffsetString(eventDate, checkInTime, timeZone) : "";

    return {
        startTime,
        endTime,
        eventDateTimeOffset,
        checkInTimeOffset,
    };
}

export const getBrowserTimeZone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

export const formatUTCToDateInTimeZone = (utcDateTimeString: string, timeZone?: string): Date | null => {
  if (!utcDateTimeString) return null;

  const parsed = dayjs.utc(utcDateTimeString).tz(timeZone || getBrowserTimeZone());
  if (!parsed.isValid()) return null;

  return dayjs(parsed.format("YYYY-MM-DD")).toDate();
};

export const formatUTCToTimeInTimeZone = (utcDateTimeString: string, timeZone?: string): string => {
  if (!utcDateTimeString) return "";

  const parsed = dayjs.utc(utcDateTimeString).tz(timeZone || getBrowserTimeZone());
  if (!parsed.isValid()) return "";

  return parsed.format("HH:mm:ss");
};

const getCommonDays = (ranges: { from_date: Date; to_date: Date }[]): Date[] => {
  if (ranges.length === 0) return [];

  const fromDates = ranges.map(r => r.from_date);
  const toDates = ranges.map(r => r.to_date);

  const overlapStart = new Date(Math.max(...fromDates.map(d => d.getTime())));
  const overlapEnd = new Date(Math.min(...toDates.map(d => d.getTime())));

  const result: Date[] = [];
  if (overlapStart <= overlapEnd) {
    for (let d = new Date(overlapStart); d <= overlapEnd; d.setDate(d.getDate() + 1)) {
      result.push(new Date(d)); // clone để không bị mutate khi vòng for tiếp tục
    }
  }
  return result;
};

const formatTimeForTimeInput = (dateString: string): string => {
  if (!dateString) return "";
  const d = new Date(dateString);
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

const timeHelper = {
    formatTime,
    convertLocalToUTC,
    convertUTCToLocal,
    mergeDateAndTimeToISOString,
    mergeDateAndTimeToDateTimeOffsetString,
    buildEventDateTimeFields,
    getCommonDays,
    formatTimeForTimeInput
}

export default timeHelper;
